/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Select,
  Stack,
  Textarea,
  TextInput,
  Button,
  Group,
  ActionIcon,
  Text,
  Table,
  Divider,
  LoadingOverlay,
  Container,
  Paper,
  Badge
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { masterQuestionService } from 'services/lia/masterQuestionService';
import { CreateMasterQuestionDto } from 'services/api';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import RangeTemplateSelector from 'components/RangeTemplateSelector';
import CategorySelector from '../components/CategorySelector';

const questionsPath = '/app/lia/questions/master-questions/list';
const groupSelectionPath = '/app/lia/questions/master-questions';

const buildGroupReturnPath = (requestedPath: string | undefined, groupId: string) => {
  const [pathname, query = ''] = requestedPath?.split('?') || [];
  const params = new URLSearchParams(pathname === questionsPath ? query : '');
  params.delete('groups');
  params.set('group', groupId);
  return `${questionsPath}?${params.toString()}`;
};

const rangeItemSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  value: z.string().min(1, 'Value is required'),
  sequence: z.number().optional()
});

const questionSchema = z.object({
  Title: z.string().min(1, 'Question is required'),
  Category: z.array(z.string()).default([]),
  Description: z.string().optional(),
  Type: z.enum(['Choose One', 'Range', 'Free-text']),
  MsGroupQuestionID: z.string().min(1, 'Group Question is required'),
  Sequence: z.number().optional().default(0),
  rangeItem: z.array(rangeItemSchema).optional(),
  SkalaCategory: z.string().optional()
});

type FormData = z.infer<typeof questionSchema>;

const AddMasterQuestionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('group') || '';
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);

  const requestedReturnPath = (location.state as { returnTo?: string } | null)?.returnTo;
  const returnPath = groupId
    ? buildGroupReturnPath(requestedReturnPath, groupId)
    : groupSelectionPath;
  const items = [
    { title: 'Questions', href: returnPath },
    { title: 'New Question', href: '' }
  ];

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      rangeItem: [],
      Sequence: 1,
      Category: [],
      MsGroupQuestionID: groupId
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rangeItem'
  });

  const questionType = watch('Type');

  useEffect(() => {
    if (!groupId) {
      navigate(groupSelectionPath, { replace: true });
    }
  }, [groupId, navigate]);

  const createMutation = useMutation({
    mutationFn: (data: FormData) => {
      const createDto: CreateMasterQuestionDto = {
        Title: data.Title,
        Category: data.Category.join(', '),
        Description: data.Description,
        Type: data.Type,
        MsGroupQuestionID: data.MsGroupQuestionID,
        Sequence: data.Sequence || 1,
        SkalaCategory: data.SkalaCategory,
        RangeItem:
          data.Type === 'Range' && data.rangeItem
            ? data.rangeItem.map((item, index) => ({
                ...item,
                sequence: item.sequence || index + 1
              }))
            : undefined
      };

      return masterQuestionService.create(createDto);
    },
    onSuccess: () => {
      navigate(returnPath);
      showSuccessNotification({
        title: 'Created Successfully!',
        message: 'Your question has been created successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to create question',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const processSubmit = () => {
    if (formData) {
      createMutation.mutate(formData);
    }
    close();
  };

  const handleTemplateSelect = (templateItems: any[]) => {
    if (fields.length > 0) {
      remove();
    }

    if (templateItems && templateItems.length > 0) {
      templateItems.forEach((item: any) => {
        append({
          label: item.label,
          value: item.value,
          sequence: item.sequence || 1
        });
      });
    }
  };

  if (!groupId) {
    return null;
  }

  return (
    <Container fluid>
      <PageTitle title='Add Question' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Submit'
        statusProcess='submit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processSubmit}
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay visible={createMutation.isPending} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap='md'>
            <Controller
              name='Title'
              control={control}
              render={({ field }) => (
                <Textarea
                  autosize
                  minRows={2}
                  maxRows={10}
                  label='Question'
                  placeholder='Input question...'
                  error={errors.Title?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='Category'
              control={control}
              render={({ field }) => (
                <CategorySelector
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.Category?.message}
                  label='Question Categories'
                  placeholder='Select existing or add new categories'
                  required={false}
                />
              )}
            />

            <Controller
              name='SkalaCategory'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Skala Category'
                  placeholder='Input skala category...'
                  error={errors.Title?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='Description'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Question Description'
                  placeholder='Input description...'
                  description='Optional description for the question'
                  {...field}
                />
              )}
            />

            <Controller
              name='Type'
              control={control}
              render={({ field }) => (
                <Select
                  label='Type of Question'
                  placeholder='Select type'
                  required
                  data={[
                    { value: 'Choose One', label: 'Choose One' },
                    { value: 'Range', label: 'Range' },
                    { value: 'Free-text', label: 'Free-text' }
                  ]}
                  error={errors.Type?.message}
                  {...field}
                />
              )}
            />

            {questionType === 'Range' && (
              <Stack gap='md'>
                <Group justify='space-between'>
                  <Text size='sm' fw={500}>
                    Range Items
                  </Text>
                  <RangeTemplateSelector onSelect={handleTemplateSelect} />
                </Group>

                <Table
                  verticalSpacing='sm'
                  horizontalSpacing='sm'
                  withTableBorder={false}
                  withColumnBorders={false}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Label</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th>Sequence</Table.Th>
                      <Table.Th w={40} /> {/* Column for delete button */}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {fields.map((field, index) => (
                      <Table.Tr key={field.id}>
                        <Table.Td>
                          <Controller
                            name={`rangeItem.${index}.label`}
                            control={control}
                            render={({ field }) => (
                              <TextInput
                                placeholder='e.g., Poor, Fair, Good'
                                error={errors.rangeItem?.[index]?.label?.message}
                                {...field}
                              />
                            )}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Controller
                            name={`rangeItem.${index}.value`}
                            control={control}
                            render={({ field }) => (
                              <TextInput
                                placeholder='e.g., 1, 2, 3'
                                error={errors.rangeItem?.[index]?.value?.message}
                                {...field}
                              />
                            )}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Controller
                            name={`rangeItem.${index}.sequence`}
                            control={control}
                            defaultValue={index + 1}
                            render={({ field: { onChange, value, ...rest } }) => (
                              <TextInput
                                placeholder='Display order'
                                type='number'
                                value={value?.toString() || (index + 1).toString()}
                                onChange={(e) => onChange(parseInt(e.target.value) || index + 1)}
                                {...rest}
                              />
                            )}
                          />
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon color='red' variant='subtle' onClick={() => remove(index)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>

                <Button
                  variant='light'
                  color='grape'
                  leftSection={<IconPlus size={16} />}
                  onClick={() =>
                    append({
                      label: '',
                      value: '',
                      sequence: fields.length + 1
                    })
                  }
                  mt='xs'
                >
                  Add Range Item
                </Button>
              </Stack>
            )}

            {questionType === 'Range' && <Divider orientation='horizontal' />}

            <Controller
              name='MsGroupQuestionID'
              control={control}
              render={({ field }) => <input type='hidden' {...field} />}
            />

            {/* <Controller
              name='Sequence'
              control={control}
              render={({ field: { onChange, value, ...rest } }) => (
                <TextInput
                  label='Sequence'
                  placeholder='Question display order (e.g., 1)'
                  type='number'
                  description='Order in which the question appears'
                  value={value?.toString() || '1'}
                  onChange={(e) => onChange(parseInt(e.target.value) || 1)}
                  {...rest}
                />
              )}
            /> */}
          </Stack>

          <Group justify='flex-end' mt='xl'>
            <SecondaryButton text='Cancel' onClick={() => navigate(returnPath)} mr={10} />
            <PrimaryButton
              text='Save'
              type='submit'
              loading={isSubmitting || createMutation.isPending}
            />
          </Group>
        </form>
      </div>
    </Container>
  );
};

export default AddMasterQuestionPage;
